import { getAccessToken, clearSession } from '@/lib/auth/session';

// Use relative URLs — Next.js rewrites proxy /api/* to the backend.
// Falls back to absolute URL for SSR (server components don't have a window.location).
const API_BASE_URL = typeof window !== 'undefined' ? '' : (process.env.NEXT_PUBLIC_API_URL || 'http://localhost:3001');

export class ApiClientError extends Error {
  constructor(
    public status: number,
    message: string,
    public code?: string,
    public body?: unknown,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

// Error codes that mean "the user's *primary* session is bad" — these
// should clear the session and bounce to /login. Anything else (including
// 401s that mean "the session is fine but the user needs to grant an
// additional Graph scope") must bubble up as a regular error so the UI
// can show a specific prompt without nuking the session.
const SESSION_FATAL_CODES = new Set(['UNAUTHENTICATED', 'INVALID_TOKEN', 'USER_NOT_FOUND']);

/**
 * `/login` carrying where the person was (2026-10-08 follow-up): a session
 * that dies mid-page used to land on a bare `/login`, so signing back in
 * dropped them on the default page instead of the matter they had open.
 * The login page validates `returnTo` before using it.
 */
export function loginUrlReturningHere(): string {
  const { pathname, search } = window.location;
  if (!pathname || pathname.startsWith('/login') || pathname.startsWith('/auth/')) return '/login';
  return `/login?returnTo=${encodeURIComponent(pathname + search)}`;
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (response.status === 401) {
    // Peek at the body's error.code to decide whether this is a session
    // failure or a scope/permission failure that we should surface inline.
    const errorBody = await response.text();
    let code: string | undefined;
    let message: string | undefined;
    try {
      const parsed = JSON.parse(errorBody);
      code = parsed?.error?.code;
      message = parsed?.error?.message;
    } catch {
      // not JSON — treat as session-fatal (no token, etc.)
    }

    const isSessionFatal = !code || SESSION_FATAL_CODES.has(code);
    if (isSessionFatal) {
      clearSession();
      if (typeof window !== 'undefined') {
        window.location.href = loginUrlReturningHere();
      }
    }

    throw new ApiClientError(401, message || errorBody || 'Unauthorized', code);
  }

  // 304 Not Modified — the body is empty, but it's a SUCCESS signal meaning
  // "use your cached copy". The fetch API doesn't surface the previously
  // cached body here (it would on `cache: 'default'` w/ HTTP cache + ETag, but
  // react-query polls bypass HTTP cache). The API now disables ETag globally
  // (apps/api/src/app.ts), so this path should not fire in practice; we
  // handle it defensively here so an upstream proxy/CDN can never brick the
  // dashboard.
  //
  // Throwing on 304 would break react-query: the query has cached data, but
  // the next refetch returns an error and (if `retry > 0`) restarts the
  // request, contributing to the React #185 notify storm on /cases. Return
  // `undefined as T` so the observer treats it as fresh-but-empty rather than
  // an error.
  if (response.status === 304) {
    return undefined as T;
  }

  if (!response.ok) {
    const errorBody = await response.text();
    let code: string | undefined;
    let messageFromBody: string | undefined;
    let parsedBody: unknown;
    try {
      const parsed = JSON.parse(errorBody);
      code = parsed?.error?.code;
      // Parse the human message out of the standard { success, error: { code,
      // message } } envelope so toasts read legibly instead of dumping raw
      // JSON (QA saw a raw "…submission…false…" body). Mirrors the 401 branch.
      messageFromBody = parsed?.error?.message;
      parsedBody = parsed;
    } catch {
      // not JSON
    }
    throw new ApiClientError(
      response.status,
      messageFromBody || errorBody || `HTTP ${response.status}: ${response.statusText}`,
      code,
      parsedBody,
    );
  }

  return response.json() as Promise<T>;
}

function getHeaders(): HeadersInit {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const token = getAccessToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  return headers;
}

function buildUrl(path: string, params?: Record<string, string | undefined>): string {
  const base = API_BASE_URL || (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3001');
  const url = new URL(`${API_BASE_URL}${path}`, base);
  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined) {
        url.searchParams.set(key, value);
      }
    }
  }
  return url.toString();
}

/**
 * Conditional-GET memo (2026-10-07) — the last 200 body + its ETag per URL,
 * for the routes that answer `If-None-Match` cheaply (the case checkpoint
 * list and step card, which the case page polls). Keyed by URL AND bearer
 * token, so a different session never replays another's body. Bounded: the
 * oldest entry is evicted past `CONDITIONAL_MEMO_MAX`.
 */
const CONDITIONAL_MEMO_MAX = 60;
const conditionalMemo = new Map<string, { etag: string; body: unknown }>();

function memoKey(url: string): string {
  return `${getAccessToken() ?? ''} ${url}`;
}

/** Test seam. */
export function __clearConditionalMemo(): void {
  conditionalMemo.clear();
}

export const apiClient = {
  async get<T>(path: string, params?: Record<string, string | undefined>): Promise<T> {
    const response = await fetch(buildUrl(path, params), {
      method: 'GET',
      headers: getHeaders(),
    });
    return handleResponse<T>(response);
  },

  /**
   * GET with an explicit `If-None-Match` (2026-10-07). On `304 Not Modified`
   * it resolves with the SAME body object it returned last time — so
   * react-query's structural sharing sees no change and nothing re-renders.
   * Use only for routes that emit an `ETag`; on any other route it behaves
   * exactly like `get` (no ETag → nothing memoised → no header sent).
   *
   * Sent explicitly rather than left to the browser HTTP cache: the API marks
   * these responses `no-store`, so a 304 can only ever answer a request that
   * carried the header itself — i.e. one from here, holding the body.
   */
  async getConditional<T>(path: string, params?: Record<string, string | undefined>): Promise<T> {
    const url = buildUrl(path, params);
    const key = memoKey(url);
    const memo = conditionalMemo.get(key);
    const headers = getHeaders() as Record<string, string>;
    if (memo) headers['If-None-Match'] = memo.etag;
    const response = await fetch(url, { method: 'GET', headers, cache: 'no-store' });
    if (response.status === 304) {
      if (memo) {
        // Refresh recency for the LRU bound.
        conditionalMemo.delete(key);
        conditionalMemo.set(key, memo);
        return memo.body as T;
      }
      // A 304 we cannot answer (memo evicted mid-flight) — refetch plainly.
      return apiClient.get<T>(path, params);
    }
    const body = await handleResponse<T>(response);
    const etag = response.headers?.get?.('ETag');
    conditionalMemo.delete(key);
    if (etag && body !== undefined) {
      conditionalMemo.set(key, { etag, body });
      while (conditionalMemo.size > CONDITIONAL_MEMO_MAX) {
        const oldest = conditionalMemo.keys().next().value;
        if (oldest === undefined) break;
        conditionalMemo.delete(oldest);
      }
    }
    return body;
  },

  async post<T>(path: string, body?: unknown): Promise<T> {
    const response = await fetch(buildUrl(path), {
      method: 'POST',
      headers: getHeaders(),
      body: body ? JSON.stringify(body) : undefined,
    });
    return handleResponse<T>(response);
  },

  async patch<T>(path: string, body?: unknown): Promise<T> {
    const response = await fetch(buildUrl(path), {
      method: 'PATCH',
      headers: getHeaders(),
      body: body ? JSON.stringify(body) : undefined,
    });
    return handleResponse<T>(response);
  },

  async put<T>(path: string, body?: unknown): Promise<T> {
    const response = await fetch(buildUrl(path), {
      method: 'PUT',
      headers: getHeaders(),
      body: body ? JSON.stringify(body) : undefined,
    });
    return handleResponse<T>(response);
  },

  async delete<T>(path: string): Promise<T> {
    const response = await fetch(buildUrl(path), {
      method: 'DELETE',
      headers: getHeaders(),
    });
    return handleResponse<T>(response);
  },

  /**
   * Fetch a binary response (e.g. a retained ID-document image) with the
   * Bearer auth header. The JSON helpers can't be used — the response body
   * is image/PDF bytes, not JSON. Throws ApiClientError on non-2xx so the
   * caller can branch on `.code` (e.g. IMAGE_NOT_RETAINED → "request a fresh
   * copy"). Does NOT clear the session on 401 (a binary sub-resource failing
   * auth shouldn't bounce the whole app to /login).
   */
  async getBlob(path: string): Promise<Blob> {
    const headers: Record<string, string> = {};
    const token = getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await fetch(buildUrl(path), { method: 'GET', headers });
    if (!response.ok) {
      const text = await response.text().catch(() => '');
      let code: string | undefined;
      try {
        code = JSON.parse(text)?.error?.code;
      } catch {
        // not JSON
      }
      throw new ApiClientError(
        response.status,
        text || `HTTP ${response.status}: ${response.statusText}`,
        code,
      );
    }
    return response.blob();
  },

  /**
   * POST JSON, receive binary. Mirrors `getBlob` but for endpoints whose
   * REQUEST is a JSON body and whose RESPONSE is a file — the submission
   * bundle posts the attachment refs (and their order) and gets a zip back.
   */
  async postForBlob(path: string, body: unknown): Promise<Blob> {
    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const token = getAccessToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
    const response = await fetch(buildUrl(path), {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      const text = await response.text().catch(() => '');
      let code: string | undefined;
      let message = text;
      try {
        const parsed = JSON.parse(text);
        code = parsed?.error?.code;
        message = parsed?.error?.message ?? text;
      } catch {
        // not JSON — keep the raw text
      }
      throw new ApiClientError(
        response.status,
        message || `HTTP ${response.status}: ${response.statusText}`,
        code,
      );
    }
    return response.blob();
  },

  /**
   * POST raw bytes (e.g. a photo File/Blob) with a caller-supplied
   * Content-Type — mirrors `getBlob`'s binary-aware pattern but for
   * uploads. Used by the note-transcribe flow, which sends the image's own
   * MIME type as the Content-Type rather than JSON (see
   * `apps/api/src/routes/case-info-items.ts`'s `express.raw` receiver).
   */
  async postRaw<T>(
    path: string,
    body: Blob,
    contentType: string,
    extraHeaders?: Record<string, string>,
  ): Promise<T> {
    const headers: Record<string, string> = { 'Content-Type': contentType, ...extraHeaders };
    const token = getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await fetch(buildUrl(path), {
      method: 'POST',
      headers,
      body,
    });
    return handleResponse<T>(response);
  },

  async multipart<T>(
    method: 'POST' | 'PUT',
    path: string,
    formData: FormData,
  ): Promise<T> {
    // Do NOT set Content-Type — the browser fills it in with the multipart
    // boundary. Only forward the auth header.
    const headers: Record<string, string> = {};
    const token = getAccessToken();
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    const response = await fetch(buildUrl(path), {
      method,
      headers,
      body: formData,
    });
    return handleResponse<T>(response);
  },
};
